import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, ChevronRight, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Phase 27c · Video Pack Carousel for MarketplaceHub.
 * Auto-fetches monetized packs from admin listing (uses top 8 by preview_count).
 * Hidden when empty (kill-switch OFF → 503 → empty).
 */
export default function VideoPackCarousel() {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        // Public preview: we discover monetized packs by fetching them
        // through the marketplace paid assets filtered on pack_v2 tag.
        const r = await fetch(`${API}/api/marketplace/ai-assets?asset_ai_type=video_ai&limit=20`,
          { credentials: "include" });
        if (!r.ok) return;
        const d = await r.json();
        // Filter items with pack_v2_metadata (or linked_video_pack_id)
        const packItems = (d.items || []).filter(
          (a) => a.linked_video_pack_id || (a.pack_v2_metadata && a.pack_v2_metadata.pack_v2)
        ).slice(0, 8);
        setPacks(packItems);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return null;
  if (packs.length === 0) return null;

  return (
    <section data-testid="video-pack-carousel" className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary">
          <Package className="h-3 w-3 text-[var(--color-secondary)]" />
          🎬 Premium Video Packs
        </div>
        <Link to="/videos" className="text-xs text-[var(--color-secondary)] hover:underline inline-flex items-center gap-1">
          Browse all <ChevronRight className="h-3 w-3" />
        </Link>
      </div>
      <div className="grid grid-flow-col auto-cols-[minmax(260px,1fr)] gap-3 overflow-x-auto pb-3 snap-x snap-mandatory">
        {packs.map((p) => {
          const meta = p.pack_v2_metadata || {};
          const packId = meta.pack_id || p.linked_video_pack_id;
          const priceEUR = ((p.price_eur_cents || 0) / 100).toFixed(2);
          return (
            <Link
              key={p.asset_id}
              to={packId ? `/videos/pack/${packId}` : `/marketplace/asset/${p.asset_id}`}
              data-testid={`video-pack-card-${p.asset_id}`}
              className="snap-start rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden hover:border-[var(--color-secondary)] transition-colors block"
            >
              <div className="aspect-video bg-gradient-to-br from-[var(--color-primary)]/30 to-[var(--color-secondary)]/30 flex items-center justify-center relative">
                <Package className="h-16 w-16 text-white/40" />
                <div className="absolute top-2 left-2 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#161616]-500/40 text-white border border-[#2A2A2A]-400/60 backdrop-blur">
                  <Sparkles className="h-2.5 w-2.5" /> AI Pack
                </div>
              </div>
              <div className="p-4 space-y-2">
                <h3 className="text-sm font-semibold line-clamp-2">
                  {p.title}
                </h3>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-text-secondary">
                    {meta.video_count || 0} videos
                  </span>
                  <span className="text-[var(--color-secondary)] font-semibold">${priceEUR}</span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
